import { useEffect, useState } from "react";
import { Stethoscope, CheckCircle, XCircle, Copy, CaretDown, CaretRight } from "@phosphor-icons/react";
import { stateDiagnoseStates, stateDiagnose } from "../lib/api";
import { toast } from "sonner";

const CHECK_LABELS = {
  crawling_source_present: "Crawler source",
  crawling_source_active: "Source active",
  parser_mapped: "Parser wired",
  has_colleges: "Colleges",
  has_seat_matrix: "Seat matrix",
  has_allotments: "Allotments",
  predictor_cutoffs: "Predictor cutoffs",
  in_predictor_body_selector: "In body selector",
  predictor_categories_in_filter_cache: "Category filter",
  fee_data_present: "Fee data",
  search_indexed: "Search indexed",
  open_state_mapping_ok: "Open-state map",
  has_documents: "Documents",
};

const PRED_ROWS = [
  ["cutoff_summary_rows", "Predictor rows (cutoff_summary)"],
  ["cutoff_fee_bucket_rows", "…with fee_bucket (fee matrix)"],
  ["counselling_results_rows", "counselling_results (fallback)"],
  ["in_predictor_body_selector", "In COUNSELLING BODY list ← this screen"],
  ["filter_cache_has_body", "Body in filter_options_cache"],
  ["colleges_total", "Colleges (by state)"],
  ["colleges_with_annual_fees", "Colleges w/ annual_fees"],
  ["colleges_with_fees_csv", "Colleges w/ fees_csv_data"],
  ["colleges_with_search_blob", "Colleges search-indexed"],
  ["is_open_state", "Open state?"],
  ["open_state_mapping_db", "open_state_mapping cats"],
  ["mcc_cutoffs_rows_for_state", "mcc_cutoffs (college pages)"],
  ["counselling_schedules", "Schedules"],
];

function Check({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
      data-testid={`diag-check-${label}`}>
      {ok ? <CheckCircle size={13} weight="bold" /> : <XCircle size={13} weight="bold" />}{label}
    </span>
  );
}

export default function StateDiagnostics() {
  const [open, setOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [env, setEnv] = useState("");
  const [sel, setSel] = useState("");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compareRaw, setCompareRaw] = useState("");
  const [compare, setCompare] = useState(null);

  useEffect(() => {
    if (!open || states.length) return;
    stateDiagnoseStates().then((r) => {
      setStates(r.data.states || []);
      setEnv(r.data.env || "");
    }).catch(() => toast.error("Could not load states"));
  }, [open, states.length]);

  const run = async () => {
    if (!sel) { toast.error("Pick a state"); return; }
    setBusy(true);
    try {
      const r = await stateDiagnose({ short: sel });
      setRep(r.data);
    } catch { toast.error("Diagnose failed"); }
    setBusy(false);
  };

  const copyReport = () => {
    if (!rep) return;
    navigator.clipboard.writeText(JSON.stringify(rep, null, 2));
    toast.success("Report JSON copied — run in the other environment & paste below to compare");
  };

  const applyCompare = () => {
    try {
      const o = JSON.parse(compareRaw);
      if (!o.counts) throw new Error("no counts");
      setCompare(o);
      toast.success(`Comparing against ${o.env || "pasted report"}`);
    } catch { toast.error("Paste a valid diagnose report JSON"); }
  };

  return (
    <div className="border-2 border-zinc-200 rounded-xl overflow-hidden bg-white mb-5" data-testid="state-diagnostics-panel">
      <button onClick={() => setOpen(!open)} data-testid="state-diagnostics-toggle"
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-50 hover:bg-zinc-100 transition-colors">
        <span className="inline-flex items-center gap-2 text-sm font-black text-[#09090B]">
          <Stethoscope size={18} weight="bold" className="text-[#6043EF]" /> State Diagnostics
          <span className="text-[10px] font-semibold text-zinc-500 normal-case">— preview vs prod config/data trace</span>
        </span>
        {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
      </button>

      {open && (
        <div className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-indigo-50 text-indigo-700" data-testid="diag-env">{env || "…"}</span>
            <select value={sel} onChange={(e) => setSel(e.target.value)} data-testid="diag-state-select"
              className="border-2 border-zinc-200 rounded-lg px-3 py-1.5 text-sm font-semibold bg-white focus:border-[#6043EF] outline-none">
              <option value="">Select a state…</option>
              {states.map((s) => (
                <option key={s.short} value={s.short}>
                  {s.state} ({s.short}){s.parser_body ? "" : " · no parser"}
                </option>
              ))}
            </select>
            <button onClick={run} disabled={busy} data-testid="diag-run-btn"
              className="px-4 py-1.5 rounded-lg bg-[#6043EF] text-white text-sm font-bold hover:bg-[#4e35c9] disabled:opacity-50 transition-colors">
              {busy ? "Diagnosing…" : "Diagnose"}
            </button>
            {rep && (
              <button onClick={copyReport} data-testid="diag-copy-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-zinc-200 text-sm font-bold text-zinc-600 hover:border-[#6043EF] transition-colors">
                <Copy size={14} weight="bold" /> Copy JSON
              </button>
            )}
          </div>

          {rep && (
            <div className="space-y-4" data-testid="diag-report">
              <div className={`rounded-lg px-3 py-2 text-sm font-bold ${rep.all_ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="diag-verdict">
                {rep.resolved.state} · body <code>{rep.resolved.body}</code> · parser {rep.resolved.parser || "—"} — {rep.all_ok ? "fully configured ✓" : "missing pieces ⚠"}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {Object.entries(rep.checks).map(([k, v]) => <Check key={k} ok={v} label={CHECK_LABELS[k] || k} />)}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="diag-counts-table">
                  <thead>
                    <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400 border-b border-zinc-200">
                      <th className="py-1.5 pr-4">Collection</th>
                      <th className="py-1.5 pr-4 text-right">{rep.env?.startsWith("PROD") ? "prod" : "this env"}</th>
                      {compare && <th className="py-1.5 pr-4 text-right">{compare.env?.startsWith("PROD") ? "prod" : "other"}</th>}
                      {compare && <th className="py-1.5 pr-4 text-right">Δ</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(rep.counts).map(([k, v]) => {
                      const ov = compare?.counts?.[k];
                      const delta = compare ? v - (ov ?? 0) : null;
                      return (
                        <tr key={k} className="border-b border-zinc-100" data-testid={`diag-count-${k}`}>
                          <td className="py-1.5 pr-4 font-medium text-zinc-700">{k}</td>
                          <td className={`py-1.5 pr-4 text-right font-mono ${v > 0 ? "text-zinc-900" : "text-rose-500 font-bold"}`}>{v}</td>
                          {compare && <td className={`py-1.5 pr-4 text-right font-mono ${(ov ?? 0) > 0 ? "text-zinc-900" : "text-rose-500 font-bold"}`}>{ov ?? 0}</td>}
                          {compare && <td className={`py-1.5 pr-4 text-right font-mono font-bold ${delta === 0 ? "text-zinc-400" : delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>{delta > 0 ? "+" : ""}{delta}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="text-[11px] text-zinc-500">
                <span className="font-bold">Years:</span>{" "}
                {Object.entries(rep.years).map(([k, ys]) => `${k}: ${ys.length ? ys.join(",") : "—"}`).join("  ·  ")}
              </div>

              {rep.predictor && (
                <div className="border-2 border-indigo-100 rounded-lg p-3 bg-indigo-50/40" data-testid="diag-predictor">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 mb-2">Predictor · Fees · Open-state · Pages footprint</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                    {PRED_ROWS.map(([k, label]) => {
                      const v = rep.predictor[k];
                      const disp = Array.isArray(v) ? (v.length ? v.join(", ") : "—")
                        : typeof v === "boolean" ? (v ? "yes" : "no")
                        : (v === null || v === undefined ? "—" : v);
                      const bad = (typeof v === "number" && v === 0) || v === false || v === null;
                      return (
                        <div key={k} className="flex items-center justify-between gap-2 text-[12px] border-b border-indigo-100/60 py-0.5" data-testid={`diag-pred-${k}`}>
                          <span className="text-zinc-600">{label}</span>
                          <span className={`font-mono font-semibold ${bad ? "text-rose-500" : "text-zinc-900"}`}>{String(disp)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t border-zinc-100 pt-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">Compare with other environment</div>
                <p className="text-[11px] text-zinc-500 mb-2">Run this same state in the other environment (preview/prod), hit <b>Copy JSON</b> there, and paste it here to see a side-by-side Δ.</p>
                <textarea value={compareRaw} onChange={(e) => setCompareRaw(e.target.value)} data-testid="diag-compare-input"
                  placeholder="Paste the other environment's report JSON…" rows={3}
                  className="w-full border-2 border-zinc-200 rounded-lg px-3 py-2 text-xs font-mono focus:border-[#6043EF] outline-none" />
                <button onClick={applyCompare} data-testid="diag-compare-btn"
                  className="mt-2 px-3 py-1.5 rounded-lg border-2 border-zinc-200 text-sm font-bold text-zinc-600 hover:border-[#6043EF] transition-colors">Compare</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
